import { useRef } from "react";
import { Box, Typography, Divider, Paper } from "@mui/material";
import { calculateAge } from "../../utility/general";

/**
 * A helper component to add print-specific styles.
 * Include this once in your main layout.
 */
export const PrintStyles = () => (
  <style jsx global>{`
    @media print {
      /* Hide all non-report elements */
      .no-print {
        display: none !important;
      }
      
      /* Reset the layout for printing */
      .print-container {
        display: block !important;
      }

      .print-sidebar {
        position: static !important;
        width: 100% !important;
        margin-bottom: 2rem;
      }
      
      .print-main-content {
        page-break-before: auto;
      }

      /* Ensure test panel cards don't break across pages */
      .print-card {
        page-break-inside: avoid !important;
        margin-bottom: 1.5rem;
      }

      /* No box shadows in print */
      .print-card, .print-card-header {
        box-shadow: none !important;
        border: 1px solid #ccc;
      }
    }
  `}</style>
);

/**
 * TemplateProPrintFriendly
 * A two-column layout on-screen that collapses to a single
 * column for printing.
 */
export default function TemplateAlt1({
  reportData,
  color,
  letterheadUrl,
  labName,
  labLocation,
  renderRow
}) {
  const reports = reportData?.patientTestReports || [];

  const printRef = useRef();

  const directPrint = () => {
    const printContents = printRef.current.innerHTML;

    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";

    document.body.appendChild(iframe);

    const doc = iframe.contentWindow.document;

    doc.open();
    doc.write(`
    <html>
      <head>
        <title>Print</title>
        <style>
          /* Include all your print CSS here */
          @media print {
            .no-print { display: none !important; }
            .print-container { display: block !important; }
            .print-sidebar { position: static !important; width: 100% !important; }
            .print-card { page-break-inside: avoid !important; border: 1px solid #ccc; }
            .print-card-header { box-shadow: none !important; }
          }
        </style>
      </head>
      <body>
        ${printContents}
      </body>
    </html>
  `);
    doc.close();

    iframe.onload = () => {
      // Wait for images & MUI Paper to render
      setTimeout(() => {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
        document.body.removeChild(iframe);
      }, 500);
    };
  };



  return (
    <Box
      className="print-container" // Class for print stylesheet
      ref={printRef}
      sx={{
        display: "flex",
        flexDirection: { xs: 'column', md: 'row' },
        gap: 3,
        p: 2,
      }}
    >

      {/* 1. Sidebar (Sticky on-screen, static on-print) */}
      <Box
        className="print-sidebar" // Class for print stylesheet
        sx={{
          width: { xs: '100%', md: 300 },
          flexShrink: 0,
          position: { md: 'sticky' },
          top: 24,
          alignSelf: 'flex-start',
        }}
      >
        {letterheadUrl && (
          <Box
            component="img"
            src={letterheadUrl}
            alt="letterhead"
            sx={{ width: 100, height: 100, objectFit: "contain", mb: 2 }}
            data-letterhead="true"
          />
        )}
        <Typography variant="h6" sx={{ color, fontWeight: 800 }}>
          {labName}
        </Typography>
        {labLocation && (
          <Typography variant="body2" sx={{ color: "#666", mb: 2 }}>
            {labLocation}
          </Typography>
        )}

        <Divider sx={{ mb: 2 }} />

        <Typography variant="overline" sx={{ display: 'block', color: '#555' }}>
          Patient
        </Typography>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
          {reportData?.patientFullName || (reportData?.walkInPatient?.firstName + ' ' + reportData?.walkInPatient?.lastName) || "N/A"}
        </Typography>

        <Typography variant="overline" sx={{ display: 'block', color: '#555', mt: 2 }}>
          Age
        </Typography>
        <Typography variant="body1" sx={{ fontWeight: 600 }}>
          {calculateAge(reportData?.walkInPatient?.dateOfBirth)}
        </Typography>

        <Typography variant="overline" sx={{ display: 'block', color: '#555', mt: 2 }}>
          Gender
        </Typography>
        <Typography variant="body1" sx={{ fontWeight: 600 }}>
          {reportData.gender || reportData?.walkInPatient?.gender || "N/A"}
        </Typography>
      </Box>

      {/* 2. Main Content (Flows normally) */}
      <Box className="print-main-content" sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="h5"
          sx={{ mb: 2, fontWeight: 700 }}
          className="no-print" // Hide this title on print
        >
          Laboratory Results
        </Typography>

        {reports.map((r) => (
          <Paper
            key={r.id}
            className="print-card" // Class for print stylesheet
            variant="outlined"
            sx={{
              mb: 2,
              borderRadius: 2,
              overflow: "hidden",
              borderColor: '#e0e0e0'
            }}
          >
            <Box
              className="print-card-header"
              sx={{ background: "#f9f9f9", p: 1.5, borderBottom: '1px solid #e0e0e0' }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: color }}>
                {r.internalLabService?.name}
              </Typography>
            </Box>
            <Box sx={{ p: 1.5 }}>
              {renderRow(r)}
            </Box>
          </Paper>
        ))}
      </Box>
    </Box>
  );
}



